import Sanity, { SanityClient } from '@sanity/client';
import { ICarouselBanner, ISimpleBanner } from '~/store/cms_content/types';
import { GamePlatform } from '~/store/games/types';

export interface IRequiredGameItem {
  asset: string;
  baseId: string;
  contractAddress: string;
  itemName: string;
}

export interface ISanityGame {
  name: string;
  displayName: string;
  isReleased: boolean;
  longDescription: string;
  developer: string;
  genre: string;
  developmentStatus: string;
  websiteLink: string;
  discordLink: string;
  platform: GamePlatform[];
  titleImage: string;
  trailer: string;
  trailerThumbnail: string;
  carouselImages: string[];
  gamePageHeaderLogo: string;
  gamePageBackground: string;
  aboutUsImage: string;
  showGamePage: boolean;
  playLink: string;
  requireAuthToPlay: boolean;
  route: string;
  collection: string;
  codeName: string;
  usesGalaChain: boolean;
  linkImage: string;
  installerUrlWin: string;
  installerUrlMac: string;
  isWinReleased: boolean;
  isMacReleased: boolean;
  isReleasedInternally: boolean;
  inventoryDisclaimer: string;
  latestWindowsVersion: string;
  latestMacVersion: string;
  showActivePlayers: boolean;
  timePlayerIsActive: number;
  activePlayerCountInDataBricks: boolean;
  hasItemRequiredToPlay: boolean;
  requiredItemIds: IRequiredGameItem[];
  requiredItemUrl: string;
  hasApkFile?: boolean;
  apkLink?: string;
  hasIOSDownload?: boolean;
  iOSDownloadLink?: string;
  customGyriGachaLoadingVideo?: string;
}

export interface ISanityHomepageTakeOver {
  name: string;
  enabled: boolean;
  logo: string;
  startDate: string;
  endDate: string;
  bgImageDesktop: string;
  bgImageMobile: string;
  noVideoMobile: boolean;
  bgVideo: string;
  ctaColor: string;
  ctaLink: string;
  ctaText: string;
  ctaTextColor: string;
  customFont: string;
  customStyles: string;
  glowColor: string;
  headline: string;
  subhead: string;
  textColor: string;
  textShadow: boolean;
}

// Sanity.io // SanityCMS
class SanityService {
  client: SanityClient;
  private readonly queryCache: Record<
    string,
    Promise<unknown> | undefined
  > = {};

  constructor() {
    this.client = Sanity({
      projectId: process.env.sanityProjectId,
      dataset: process.env.sanityDataset,
      apiVersion: process.env.sanityApiVersion, // use current UTC date - see "specifying API version"!
      token: process.env.sanityToken, // or leave blank for unauthenticated usage
      useCdn: true, // `false` if you want to ensure fresh data
    });
  }

  async handleFetch(query: string, cacheKey: string) {
    if (this.queryCache[cacheKey]) {
      return this.queryCache[cacheKey];
    }

    this.queryCache[cacheKey] = this.client.fetch(query);

    try {
      return await this.queryCache[cacheKey];
    } catch (err) {
      delete this.queryCache[cacheKey];
      throw err;
    }
  }

  async getInventoryCollections() {
    const query = `*[_type == "inventoryCollection"] { name, "inventoryImage":inventoryImage.asset->url }`;
    const results = await this.handleFetch(query, query);

    return results as Array<{
      name: string;
      inventoryImage: string;
    }>;
  }

  async getSimpleBanners(locale: string): Promise<ISimpleBanner[]> {
    const now = new Date().toISOString();

    const banners = (await this.handleFetch(
      `*[
      _type == "simpleBanner" 
      && enabled == true
      && (startDate == null || dateTime('${now}') > dateTime(startDate))
      && (endDate == null ||   dateTime('${now}') < dateTime(endDate))
    ] {
      name,
      "alt": coalesce(alt.${locale}, alt.en),
      "desktopBannerImage":desktopBannerImage.asset->url,
      "mobileBannerImage":mobileBannerImage.asset->url,
      internalRoute,
      showOnMain,
      showOnStore
    }`,
      `simple_banners_${locale}`,
    )) as ISimpleBanner[];

    return banners;
  }

  async getHomepageTakeOvers(locale: string): Promise<ISanityHomepageTakeOver> {
    const now = new Date().toISOString();

    const homepageTakeover = (await this.handleFetch(
      `*[
      _type == "HomepageTakeover" 
      && enabled == true
      && (startDate == null || dateTime('${now}') > dateTime(startDate))
      && (endDate == null ||   dateTime('${now}') < dateTime(endDate))
    ][0] {
      name,
      "logo":logo.asset->url,
      startDate,
      endDate,
      "bgImageDesktop":bgImageDesktop.asset->url,
      "bgImageMobile":bgImageMobile.asset->url,
      "bgVideo":bgVideo.asset->url,
      noVideoMobile,
      "ctaColor":ctaColor.hex,
      ctaLink,
      "ctaText":coalesce(ctaText.${locale}, ctaText.en),
      "ctaTextColor":ctaTextColor.hex,
      customFont,
      customStyles,
      "glowColor":glowColor.hex,
      "headline":coalesce(headline.${locale}, headline.en),
      "subhead":coalesce(subhead.${locale}, subhead.en),
      "textColor":textColor.hex,
      textShadow
    }`,
      `homepage_takeovers_${locale}`,
    )) as ISanityHomepageTakeOver;

    return homepageTakeover;
  }

  async getCarouselBanners(locale: string) {
    const now = new Date().toISOString();

    const banners = (await this.handleFetch(
      `*[
      _type == "banner" 
      && enabled == true
      && (startDate == null || dateTime('${now}') > dateTime(startDate))
      && (endDate == null || dateTime('${now}') < dateTime(endDate))
    ] {
      name,
      "title": coalesce(title.${locale}, title.en),
      "description": coalesce(description.${locale}, description.en),
      "tagline": coalesce(tagline.${locale}, tagline.en),
      size,
      bannerImageClass,
      "bannerImageMin":bannerImageMin.asset->url,
      "bannerImage":bannerImage.asset->url,
      "learnMoreText": coalesce(learnMoreText.${locale}, learnMoreText.en),
      learnMoreLink,
      playLink,
      order,
      whenUnauthenticated
    } | order(order asc)`,
      `carousel_banners_${locale}`,
    )) as ICarouselBanner[];

    return banners;
  }

  async getGames(locale: string) {
    const games = (await this.handleFetch(
      `*[
      _type == "game" 
    ] {
      name,
      "displayName": coalesce(displayName.${locale}, displayName.en),
      "longDescription": coalesce(longDescription.${locale}, longDescription.en),
      isReleased,
      isWinReleased,
      isMacReleased,
      developer,
      "genre": coalesce(genre.${locale}, genre.en),
      "developmentStatus": coalesce(developmentStatus.${locale}, developmentStatus.en),
      websiteLink,
      discordLink,
      platform,
      "linkImage": linkImage.asset->url,
      "trailer": trailer.asset->url,
      "trailerThumbnail": trailerThumbnail.asset->url,
      "carouselImages": carouselImages[].asset->url,
      "gamePageHeaderLogo": gamePageHeaderLogo.asset->url,
      "gamePageBackground": gamePageBackground.asset->url,
      "aboutUsImage": aboutUsImage.asset->url,
      showGamePage,
      playLink,
      hasItemRequiredToPlay,
      requiredItemIds,
      requiredItemUrl,
      requireAuthToPlay,
      route,
      collection,
      codeName,
      usesGalaChain,
      isReleasedInternally,
      "inventoryDisclaimer": coalesce(inventoryDisclaimer.${locale}, inventoryDisclaimer.en),
      "latestWindowsVersion": coalesce(latestWindowsVersion, latestVersion),
      latestMacVersion,
      installerUrlWin,
      installerUrlMac,
      showActivePlayers,
      timePlayerIsActive,
      activePlayerCountInDataBricks,
      hasApkFile,
      apkLink,
      hasIOSDownload,
      iOSDownloadLink,
      order,
      customGyriGachaLoadingVideo
    } | order(order asc)`,
      `games_${locale}`,
    )) as ISanityGame[];

    return games;
  }
}
export default new SanityService();
